import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  BuildingOffice2Icon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  DevicePhoneMobileIcon,
  DocumentTextIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  SignalIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline'
import { submitNetlifyForm } from '../../utils/netlifyForms'
import Captcha, { captchaEnabled } from './Captcha'

const icons = {
  building: BuildingOffice2Icon,
  chart: ChartBarIcon,
  chat: ChatBubbleLeftRightIcon,
  clock: ClockIcon,
  device: DevicePhoneMobileIcon,
  document: DocumentTextIcon,
  lightbulb: LightBulbIcon,
  shield: ShieldCheckIcon,
  signal: SignalIcon,
  sparkles: SparklesIcon,
  wrench: WrenchScrewdriverIcon,
}

const inputClass =
  'block w-full rounded-md border-0 px-3.5 py-2.5 text-navy-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-electric-500 sm:text-sm sm:leading-6'

const copy = {
  es: {
    next: 'Continuar', back: 'Atrás', skip: 'Omitir', send: 'Enviar', sending: 'Enviando...',
    required: 'Este dato nos ayuda a orientarte mejor', pickOne: 'Elige al menos una opción',
    name: 'Nombre', email: 'Correo electrónico', phone: 'Teléfono', company: 'Empresa (opcional)',
    invalidEmail: 'Correo no válido', fillName: 'Escribe tu nombre', fillEmail: 'Escribe tu correo', fillPhone: 'Escribe tu teléfono',
    change: 'Cambiar motivo', typeHere: 'Escribe aquí', captchaNeeded: 'Confirma que no eres un robot',
  },
  en: {
    next: 'Continue', back: 'Back', skip: 'Skip', send: 'Send', sending: 'Sending...',
    required: 'This helps us guide you better', pickOne: 'Pick at least one option',
    name: 'Name', email: 'Email', phone: 'Phone', company: 'Company (optional)',
    invalidEmail: 'Invalid email', fillName: 'Enter your name', fillEmail: 'Enter your email', fillPhone: 'Enter your phone',
    change: 'Change topic', typeHere: 'Type here', captchaNeeded: 'Please confirm you are not a robot',
  },
}

// Sigue las opciones preseleccionadas (preset) para saltar pasos ya respondidos.
function initialPath(flow, preset) {
  const path = [flow.start]
  let guard = 0
  while (guard++ < 20) {
    const step = flow.steps[path[path.length - 1]]
    const value = preset[path[path.length - 1]]
    const opt = value && step.options?.find((o) => o.value === value)
    if (!opt?.next) break
    path.push(opt.next)
  }
  return path
}

// Formulario conversacional: una pregunta a la vez, el siguiente paso depende de la respuesta,
// y cada paso puede acompañarse de video, imagen o ilustración.
export default function FlowForm({ flow, preset = {}, onSwitchFlow, onBackToStart, compact = false, className = '' }) {
  const { t, i18n } = useTranslation('contact')
  const lang = i18n.language?.startsWith('en') ? 'en' : 'es'
  const c = copy[lang]
  const L = (v) => (v && typeof v === 'object' ? v[lang] ?? v.es : v)

  const { register, handleSubmit, trigger, getValues, formState: { errors } } = useForm({ shouldUnregister: false })
  const [answers, setAnswers] = useState(preset) // respuestas de choice / multi
  const [path, setPath] = useState(() => initialPath(flow, preset))
  const [echo, setEcho] = useState('')
  const [status, setStatus] = useState('idle')
  const [multiError, setMultiError] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const [captchaError, setCaptchaError] = useState(false)

  const currentId = path[path.length - 1]
  const step = flow.steps[currentId]
  const Icon = icons[step.icon] || SparklesIcon
  const questionSteps = path.filter((id) => flow.steps[id].type !== 'contact')
  const progress = step.type === 'contact' ? 100 : Math.round((path.length / (path.length + 1)) * 92)

  const goTo = (next, echoText) => {
    setEcho(L(echoText) || '')
    setMultiError(false)
    if (next) setPath((p) => [...p, next])
  }

  const back = () => {
    setEcho('')
    setMultiError(false)
    if (path.length > 1) setPath((p) => p.slice(0, -1))
    else if (onBackToStart) onBackToStart()
  }

  const onChoose = (opt) => {
    if (opt.flow) return onSwitchFlow?.(opt.flow)
    setAnswers((a) => ({ ...a, [currentId]: opt.value }))
    goTo(opt.next, opt.echo)
  }

  const toggleMulti = (value) =>
    setAnswers((a) => {
      const cur = a[currentId] || []
      return { ...a, [currentId]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] }
    })

  const onNextMulti = () => {
    if (!step.optional && !(answers[currentId] || []).length) return setMultiError(true)
    goTo(step.next, step.echo)
  }

  const onNextField = async () => {
    if (await trigger(currentId)) goTo(step.next, step.echo)
  }

  const answerLabel = (id) => {
    const s = flow.steps[id]
    const v = s.type === 'choice' || s.type === 'multi' ? answers[id] : getValues(id)
    if (v == null || v === '' || (Array.isArray(v) && !v.length)) return ''
    const label = (val) => L(s.options?.find((o) => o.value === val)?.label) ?? val
    return Array.isArray(v) ? v.map(label).join(', ') : label(v)
  }

  const onSubmit = (values) => {
    if (values['bot-field']) return setStatus('success') // honeypot: los bots creen que funcionó
    if (captchaEnabled && !captchaToken) return setCaptchaError(true)
    const { formName, fieldMap = {}, extra = {}, motivo } = flow.submit
    const f = (key) => fieldMap[key] || key
    const summary = questionSteps
      .map((id) => ({ q: L(flow.steps[id].text), a: answerLabel(id) }))
      .filter((x) => x.a)
      .map((x) => `${x.q} ${x.a}`)
      .join(' | ')
    const payload = {
      [f('name')]: values.name,
      [f('email')]: values.email,
      [f('telephone')]: values.telephone,
      [f('company')]: values.company || '',
      [f('message')]: summary,
    }
    if (motivo) payload.motivo = L(motivo)
    if (captchaEnabled) payload['g-recaptcha-response'] = captchaToken
    Object.entries(extra).forEach(([field, stepId]) => {
      const v = answers[stepId]
      payload[field] = Array.isArray(v) ? v.join(',') : v || ''
    })
    setStatus('submitting')
    submitNetlifyForm(formName, payload)
      .then(() => setStatus('success'))
      .catch(() => setStatus('error'))
  }

  if (status === 'success') {
    return (
      <div className={`rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-xl sm:p-10 ${className}`}>
        <p className="text-xl font-bold text-navy-900">{t('form.successTitle')}</p>
        <p className="mt-2 text-base text-gray-600">{t('form.successDescription')}</p>
      </div>
    )
  }

  const fieldError = (name) => errors[name] && <p className="mt-1 text-sm text-red-600">{errors[name].message}</p>
  const primaryBtn = 'rounded-md bg-electric-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-electric-700 disabled:cursor-not-allowed disabled:opacity-60'

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={`overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl ${className}`}>
      <div className="h-1.5 w-full bg-gray-100">
        <motion.div className="h-full bg-electric-500" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
      </div>

      <div className={compact ? 'grid' : 'grid md:grid-cols-5'}>
        {/* Video / imagen / ilustración del paso */}
        <div className={`relative flex min-h-[9rem] items-center justify-center overflow-hidden bg-gradient-to-br from-navy-900 to-electric-700 ${compact ? 'min-h-[7rem]' : 'md:col-span-2 md:min-h-[26rem]'}`}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentId}
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
            >
              {step.video ? (
                <video src={step.video} poster={step.poster} autoPlay muted loop playsInline className="h-full w-full object-cover" />
              ) : step.image ? (
                <img src={step.image} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex flex-col items-center gap-3 text-white">
                  <span className="rounded-full bg-white/10 p-6 ring-1 ring-white/20">
                    <Icon className="h-14 w-14 md:h-20 md:w-20" aria-hidden="true" />
                  </span>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className={`p-6 sm:p-8 ${compact ? '' : 'md:col-span-3'}`}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentId}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.25 }}
            >
              {echo && <p className="mb-3 text-sm font-medium text-electric-600">{echo}</p>}
              <h2 className="text-2xl font-bold tracking-tight text-navy-900">{L(step.text)}</h2>
              {step.hint && <p className="mt-2 text-sm text-gray-500">{L(step.hint)}</p>}

              {step.type === 'choice' && (
                <div className="mt-6 grid gap-3">
                  {step.options.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => onChoose(opt)}
                      className={`rounded-lg border px-4 py-3 text-left text-navy-900 transition hover:border-electric-500 hover:bg-gray-50 ${
                        answers[currentId] === opt.value ? 'border-electric-600 bg-gray-50' : 'border-gray-300'
                      }`}
                    >
                      {L(opt.label)}
                    </button>
                  ))}
                </div>
              )}

              {step.type === 'multi' && (
                <div className="mt-6">
                  <div className="grid gap-3">
                    {step.options.map((opt) => {
                      const selected = (answers[currentId] || []).includes(opt.value)
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          aria-pressed={selected}
                          onClick={() => toggleMulti(opt.value)}
                          className={`rounded-lg border px-4 py-3 text-left transition ${
                            selected ? 'border-electric-600 bg-electric-600 text-white' : 'border-gray-300 text-navy-900 hover:border-electric-500'
                          }`}
                        >
                          {L(opt.label)}
                        </button>
                      )
                    })}
                  </div>
                  {multiError && <p className="mt-2 text-sm text-red-600">{c.pickOne}</p>}
                  <button type="button" onClick={onNextMulti} className={`mt-6 ${primaryBtn}`}>
                    {step.optional && !(answers[currentId] || []).length ? c.skip : c.next}
                  </button>
                </div>
              )}

              {(step.type === 'text' || step.type === 'number') && (
                <div className="mt-6">
                  {step.type === 'number' ? (
                    <input
                      type="number"
                      min="1"
                      placeholder={L(step.placeholder)}
                      className={inputClass}
                      {...register(currentId, { required: !step.optional && c.required })}
                    />
                  ) : (
                    <textarea
                      rows={4}
                      placeholder={L(step.placeholder) || c.typeHere}
                      className={inputClass}
                      {...register(currentId, { required: step.required && c.required })}
                    />
                  )}
                  {fieldError(currentId)}
                  <button type="button" onClick={onNextField} className={`mt-6 ${primaryBtn}`}>
                    {c.next}
                  </button>
                </div>
              )}

              {step.type === 'contact' && (
                <div className="mt-6 grid gap-4">
                  {status === 'error' && (
                    <p className="rounded-md bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{t('form.errorMessage')}</p>
                  )}
                  <div>
                    <input autoComplete="name" className={inputClass} placeholder={c.name} {...register('name', { required: c.fillName })} />
                    {fieldError('name')}
                  </div>
                  <div>
                    <input
                      type="email"
                      autoComplete="email"
                      className={inputClass}
                      placeholder={c.email}
                      {...register('email', { required: c.fillEmail, pattern: { value: /^\S+@\S+\.\S+$/, message: c.invalidEmail } })}
                    />
                    {fieldError('email')}
                  </div>
                  <div>
                    <input type="tel" autoComplete="tel" className={inputClass} placeholder={c.phone} {...register('telephone', { required: c.fillPhone })} />
                    {fieldError('telephone')}
                  </div>
                  <input autoComplete="organization" className={inputClass} placeholder={c.company} {...register('company')} />
                  {/* honeypot anti-spam: invisible para personas */}
                  <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" {...register('bot-field')} />
                  <div>
                    <Captcha onToken={(t) => { setCaptchaToken(t); if (t) setCaptchaError(false) }} />
                    {captchaError && <p className="mt-1 text-sm text-red-600">{c.captchaNeeded}</p>}
                  </div>
                  <button type="submit" disabled={status === 'submitting'} className={primaryBtn}>
                    {status === 'submitting' ? c.sending : c.send}
                  </button>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {(path.length > 1 || onBackToStart) && (
            <button type="button" onClick={back} className="mt-8 text-sm text-gray-500 hover:text-navy-900">
              ← {path.length > 1 ? c.back : c.change}
            </button>
          )}
        </div>
      </div>
    </form>
  )
}
